import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { UserNoteType } from './enums';
import { DBUserNoteSchema, UserNoteSchema } from './schemas';

export const UserNoteCodecConfig: TransformerConfig<
  typeof UserNoteSchema,
  typeof DBUserNoteSchema
> = {
  name: 'UserNote',
  clientSchema: UserNoteSchema,
  dbSchema: DBUserNoteSchema,
  transformers: [
    // userId + type ↔ composite userId key (type#userId)
    createTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>({
      encoder: {
        sourceFields: ['userId', 'type'] as const,
        dependencies: ['userId', 'type'] as const,
        encode: ({ userId, type }) => ({
          userId: `${type}#${userId}`,
        }),
      },
      decoder: {
        sourceFields: ['userId'] as const,
        decode: ({ userId: compositeUserId }) => {
          // istanbul ignore next -- defaults unreachable due to schema validation requiring exactly 2 parts
          const [type = '', userId = ''] = compositeUserId.split('#');

          return {
            type: type as UserNoteType,
            userId,
          };
        },
      },
    }),

    // active: boolean ↔ active: 'true' | 'false'
    createTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>({
      encoder: {
        sourceFields: ['active'] as const,
        encode: ({ active }) => ({
          active: active ? 'true' : 'false',
        }),
      },
      decoder: {
        sourceFields: ['active'] as const,
        decode: ({ active }) => ({
          active: active === 'true',
        }),
      },
    }),

    // activeBillingRelatedKey: computed index key
    // Consumes billingRelated (passes through) and active, computes activeBillingRelatedKey
    createTransformer<typeof UserNoteSchema, typeof DBUserNoteSchema>({
      encoder: {
        sourceFields: ['active', 'billingRelated'] as const,
        dependencies: ['active', 'billingRelated'] as const,
        encode: ({ active, billingRelated }) => ({
          activeBillingRelatedKey: `${active}#${billingRelated}`,
          billingRelated,
        }),
      },
      // Decoder consumes activeBillingRelatedKey to prevent it from being passed through
      decoder: {
        sourceFields: ['activeBillingRelatedKey'] as const,
        decode: () => ({}),
      },
    }),
  ],
};

export const encodeUserNoteToDb = createEncodeToDbFn(UserNoteCodecConfig);
export const decodeUserNoteFromDb = createDecodeFromDbFn(UserNoteCodecConfig);
